import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Stack,
  useTheme,
  alpha,
  Button,
} from "@mui/material";
import {
  Speed as SpeedIcon,
  Psychology as InnovationIcon,
  Diversity3 as TeamIcon,
  Timeline as ProcessIcon,
  Assignment as ProjectIcon,
  EmojiObjects as SolutionIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Utils/Footer";
import Header from "../Components/Utils/Header";
import logo from "../assets/videos/logo.mp4";

const CORE_VALUES = [
  {
    icon: SpeedIcon,
    title: "Efficiency",
    description: "We deliver high-quality solutions on time and within budget",
    color: "primary",
  },
  {
    icon: InnovationIcon,
    title: "Innovation",
    description:
      "Embracing cutting-edge technologies to solve complex challenges",
    color: "secondary",
  },
  {
    icon: TeamIcon,
    title: "Collaboration",
    description: "Working closely with clients to ensure project success",
    color: "info",
  },
];

const PROCESS_STEPS = [
  {
    icon: ProcessIcon,
    title: "Discovery",
    description: "Understanding your needs and project requirements",
    color: "success",
  },
  {
    icon: ProjectIcon,
    title: "Planning",
    description: "Developing detailed project roadmap and milestones",
    color: "warning",
  },
  {
    icon: SolutionIcon,
    title: "Execution",
    description: "Implementing solutions with precision and expertise",
    color: "error",
  },
];

const STATS = [
  { label: "Projects Completed", value: "100+" },
  { label: "Happy Clients", value: "50+" },
  { label: "Team Members", value: "15+" },
  { label: "Years Experience", value: "5+" },
];

export default function AboutPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Header active="About" />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 12 },
          bgcolor: "background.paper",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
          },
        }}
      >
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Typography variant="h3" sx={{ fontWeight: 700 }} gutterBottom>
                  Transforming Ideas into Digital Reality
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Jeremy Solutions is a leading technology company in Kenya,
                  specializing in Web Design, GIS Solutions, Digital Marketing,
                  and Art Sales. We combine innovation with expertise to deliver
                  exceptional digital solutions that drive business growth.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => navigate("/contact")}
                  sx={{
                    alignSelf: "flex-start",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Get in Touch
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="video"
                src={logo}
                autoPlay
                loop
                muted
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 4,
                  boxShadow: theme.shadows[8],
                  objectFit: "cover",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Values Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "background.default" }}>
        <Container>
          <Stack spacing={6}>
            <Stack spacing={2} sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Our Core Values
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                The principles that guide our work and relationships
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {CORE_VALUES.map((value, index) => (
                <Grid item xs={12} md={4} key={value.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        p: 4,
                        transition: "all 0.3s ease-in-out",
                        border: `1px solid ${alpha(
                          theme.palette[value.color].main,
                          0.1
                        )}`,
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: theme.shadows[8],
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(
                              theme.palette[value.color].main,
                              0.1
                            ),
                            color: `${value.color}.main`,
                          }}
                        >
                          <value.icon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {value.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {value.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          bgcolor: "background.paper",
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Container>
          <Grid container spacing={3}>
            {STATS.map((stat, index) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Stack spacing={1} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, color: "primary.main" }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {stat.label}
                    </Typography>
                  </Stack>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "background.default" }}>
        <Container>
          <Stack spacing={6}>
            <Stack spacing={2} sx={{ textAlign: "center" }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Our Process
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                How we turn your vision into reality
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {PROCESS_STEPS.map((step, index) => (
                <Grid item xs={12} md={4} key={step.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        p: 4,
                        position: "relative",
                        transition: "all 0.3s ease-in-out",
                        border: `1px solid ${alpha(
                          theme.palette[step.color].main,
                          0.1
                        )}`,
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: theme.shadows[8],
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(theme.palette[step.color].main, 0.1),
                            color: `${step.color}.main`,
                          }}
                        >
                          <step.icon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {step.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
